/**
 * Admin UI Tooltips - Moderation Rules Reference
 * 
 * This file contains tooltip content tied to the moderation playbook rules.
 * Use these tooltips throughout the admin panel to provide contextual guidance.
 */

export const moderationTooltips = {
  // Status Badges
  status: {
    pending: 'Newly submitted report. Not visible to public. Review using the Universal Moderation Checklist before approving.',
    approved: 'Report meets evidence and neutrality requirements. Visible to public if marked public.',
    rejected: 'Report does not meet platform requirements. Stored for audit, not publicly visible.',
    hidden: 'Previously approved but temporarily removed due to dispute, risk, or review. Admin-only visibility.',
  },

  // Action Buttons
  actions: {
    approve: 'Approve only if: Evidence present, no accusations, neutral language, relevant to category. Check category-specific rules before approving.',
    reject: 'Reject if: No evidence, accusations present, hate speech, or violates category-specific rules. Provide clear rejection reason.',
    hide: 'Hide temporarily if: Dispute received, legal threat, election sensitivity, or safety concern. Escalate to Root Admin if needed.',
    delete: 'Delete only if: Proven false evidence, clear rule violation, or court order. Prefer "Hide" over "Delete" during election periods.',
  },

  // Universal Moderation Checklist
  universalChecklist: {
    evidence: 'At least one piece of evidence (photo/video) must be present and relevant to the report category.',
    noAccusations: 'No explicit accusations (e.g., "corrupt", "illegal"). Description must be observational, not opinionated.',
    noHateSpeech: 'No hate speech, threats, or harassment. Remove any content that targets individuals or groups.',
    noPrivateData: 'No personal data beyond what is visible in public space. Protect privacy of individuals.',
    neutralLanguage: 'Description must state only what is visible, not assign intent or judge legality.',
  },

  // Category-Specific Rules
  categories: {
    antiEpal: {
      approve: 'Approve if: Evidence shows signage/billboards/tarpaulins on government-funded projects. Description is neutral and observational only.',
      reject: 'Reject if: No clear evidence of promotional material, accusations of illegality, or unrelated/unverifiable images.',
      disclaimer: 'Required disclaimer: "This report documents an observed promotional material on a government-funded project. It does not determine legality or intent."',
    },
    projectDelay: {
      approve: 'Approve if: Evidence shows location with limited/no visible activity. Timestamps or multiple reports provide context. Language is descriptive.',
      reject: 'Reject if: Claims of fraud, theft, or corruption. Assertions about budgets/timelines without evidence.',
      note: 'Never label as "delayed" or "abandoned". Use: "Reports submitted after expected timeline".',
    },
    ghostProject: {
      approve: 'Approve if: Evidence shows location with limited/no visible activity. Timestamps or multiple reports provide context. Language is descriptive.',
      reject: 'Reject if: Claims of fraud, theft, or corruption. Assertions about budgets/timelines without evidence.',
      note: 'Never label as "delayed" or "abandoned". Use: "Reports submitted after expected timeline".',
    },
    serviceFailure: {
      approve: 'Approve if: Evidence supports observation (e.g., photos of broken facilities). Report avoids blame or personal targeting.',
      reject: 'Reject if: Pure complaints without evidence. Abusive language toward individuals.',
    },
    complianceGap: {
      approve: 'Approve if: Evidence shows absence of required public information. Description states what is missing, not why.',
      reject: 'Reject if: Assumptions about motives or violations.',
    },
  },

  // Comment Moderation
  comments: {
    approve: 'Approve comments that: Add context/clarification, ask factual questions, or provide additional evidence.',
    reject: 'Remove comments that: Accuse individuals, contain political campaigning, encourage harassment, or declare guilt/illegality.',
    rule: 'Comments must never escalate beyond the original report. They should clarify, not accuse.',
  },

  // Community Signals
  communitySignals: {
    votes: 'Votes are used as visibility signals, NOT truth indicators. Review manually before taking action.',
    flags: 'Abuse flags trigger review when threshold reached. Do NOT auto-hide based on votes alone. Review flagged reports promptly.',
    highUpvotes: 'High upvotes indicate community interest. Still verify report meets approval criteria before relying on votes.',
    highDownvotes: 'High downvotes may indicate disagreement or brigading. Review for coordinated behavior.',
    reported: 'Reported/flagged content requires manual review. Check for brigading, verify evidence, and confirm approval criteria.',
  },

  // Dispute Handling
  disputes: {
    complaint: 'When complaint received: Do NOT reply emotionally. Review evidence, language, and moderation history. Assess legal/election sensitivity.',
    hide: 'If risk exists: Set to Hidden, escalate to Root Admin, and log in auditLogs. Do NOT delete unless ordered or clearly justified.',
    escalate: 'Escalate immediately for: National-level figures, viral attention, legal threats, media inquiries, or safety threats.',
  },

  // Election Period
  electionPeriod: {
    mode: 'During election periods: Apply stricter language review, prefer Hide over Delete, require Root Admin approval for high-profile cases.',
    highProfile: 'Require Root Admin review for: High-profile individuals, media-linked reports, COMELEC-related complaints.',
    never: 'Never: Mass-remove reports, alter data to favor any party, or publish endorsements.',
    rule: 'Visibility can wait. Legal safety cannot.',
  },

  // Language Safety
  languageSafety: {
    never: 'NEVER approve content that: Declares illegality, assigns intent/motive, labels someone corrupt/guilty, or calls for punishment.',
    should: 'SHOULD enforce: "Observed", "Reported", "Documented", "Based on uploaded evidence". Stick to observable facts only.',
  },

  // Audit & Accountability
  audit: {
    action: 'Every moderation action must record: Action taken, Admin ID, Timestamp, and clear factual reason.',
    purpose: 'Audit logs exist to: Defend VerifyPH, protect moderators, and demonstrate neutrality.',
  },

  // Final Rule
  finalRule: {
    unsure: 'If unsure, uncomfortable, or pressured: Hide the content → Escalate to Root Admin → Document everything.',
    priority: 'VerifyPH prioritizes: Public safety, legal integrity, and long-term trust. Speed is secondary to correctness.',
  },
};

/**
 * Get tooltip content for a specific moderation action or element
 */
export const getTooltip = (key: string): string | undefined => {
  const keys = key.split('.');
  let value: any = moderationTooltips;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return undefined;
    }
  }
  
  return typeof value === 'string' ? value : undefined;
};

/**
 * Get category-specific approval tooltip
 */
export const getCategoryApproveTooltip = (category: string): string => {
  const categoryKey = category.toLowerCase().replace('_', '') as keyof typeof moderationTooltips.categories;
  const categoryTooltips = moderationTooltips.categories[categoryKey];
  return categoryTooltips?.approve || moderationTooltips.actions.approve;
};

/**
 * Get category-specific rejection tooltip
 */
export const getCategoryRejectTooltip = (category: string): string => {
  const categoryKey = category.toLowerCase().replace('_', '') as keyof typeof moderationTooltips.categories;
  const categoryTooltips = moderationTooltips.categories[categoryKey];
  return categoryTooltips?.reject || moderationTooltips.actions.reject;
};
